import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MdContentCopy } from "react-icons/md";
import { toast } from 'react-toastify';
import Sidebar from '../Core/Sidebar';
import Navbar from '../Core/Navbar';
import { getPackageUpgradeListApi } from "../../Services/Operations/ProductAPI";
import * as XLSX from 'xlsx';

const PlanUpgradeReport = () => {
  const navigate = useNavigate();
  const authToken = useSelector((state) => state.user?.token);
  const userData = useSelector((state) => state.user?.userData);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [packageUpgrades, setPackageUpgrades] = useState([]);
  const [filters, setFilters] = useState({
    date: "",
    month: "",
    year: ""
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);

      const [isMobile, setIsMobile] = useState(false);
  
    // Check screen size on mount and resize
    useEffect(() => {
      const checkMobile = () => {
        const mobile = window.innerWidth < 992;
        setIsMobile(mobile);
  
        // Auto-close sidebar on mobile by default
        if (mobile) {
          setSidebarOpen(true);
        } else {
          setSidebarOpen(true);
        }
      };
  
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
  
  
  
    const closeSidebar = () => {
      if (isMobile) {
        setSidebarOpen(false);
      }
    };


  // Add toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Initialize sidebar functionality
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const sidebarBody = document.querySelector('.sidebar-body');
    
    if (sidebar) {
      if (sidebarOpen) {
        sidebar.classList.remove('close');
      } else {
        sidebar.classList.add('close');
      }
    }
    
    if (sidebarBody) {
      sidebarBody.style.height = 'calc(100vh - 70px)';
      sidebarBody.style.overflowY = 'auto';
    }

    const initialDropdownState = {
      dashboard: false,
      fundManagement: false,
      registrationTeam: false,
      incomeEarnings: false,
      bonanzaRewards: false,
      airdrop: false,
      withdrawal: false,
      settings: false
    };
    setOpenDropdowns(initialDropdownState);

    return () => {
      // Cleanup
    };
  }, [sidebarOpen]);

  // Use useMemo to calculate filtered data without causing re-renders
  const calculatedFilteredData = useMemo(() => {
    let filtered = [...packageUpgrades];
    
    // Filter by date
    if (filters.date) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_at).toISOString().split('T')[0];
        return itemDate === filters.date;
      });
    }
    
    // Filter by month
    if (filters.month) {
      filtered = filtered.filter(item => {
        const itemMonth = new Date(item.created_at).getMonth() + 1;
        return itemMonth.toString() === filters.month;
      });
    }
    
    // Filter by year
    if (filters.year) {
      filtered = filtered.filter(item => {
        const itemYear = new Date(item.created_at).getFullYear();
        return itemYear.toString() === filters.year;
      });
    }
    
    return filtered;
  }, [packageUpgrades, filters]);

  // Update filtered data and totals when calculatedFilteredData changes
  useEffect(() => {
    setFilteredData(calculatedFilteredData);
    
    // Calculate totals for filtered data
    const totalAmountValue = calculatedFilteredData.reduce((sum, item) => 
        sum + (parseFloat(item.package_amount) || 0), 0);
    const totalCountValue = calculatedFilteredData.length;
    
    setTotalAmount(totalAmountValue);
    setTotalCount(totalCountValue);
  }, [calculatedFilteredData]);

  useEffect(() => {
    // Add custom scrollbar styles for sidebar
    const style = document.createElement('style');
    style.innerHTML = `
      /* Custom scrollbar for sidebar */
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      /* Package Upgrade specific styles */
      .package-upgrade-card {
        background: rgba(30, 30, 40, 0.8);
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        margin-bottom: 20px;
        transition: all 0.3s ease;
      }
      
      .package-upgrade-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        border-color: rgba(13, 110, 253, 0.3);
      }
      
      .package-name-badge {
        background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 600;
        display: inline-block;
      }
      
      .package-amount-badge {
        background: linear-gradient(135deg, #198754 0%, #157347 100%);
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 700;
        display: inline-block;
      }
      
      .package-id-badge {
        background: rgba(108, 117, 125, 0.2);
        color: #adb5bd;
        padding: 4px 10px;
        border-radius: 15px;
        font-size: 12px;
        font-weight: 500;
      }
      
      /* Table styling for package upgrades */
      .package-upgrade-table {
        background: rgba(30, 30, 40, 0.8);
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      .package-upgrade-table thead th {
        background: linear-gradient(135deg, rgba(13, 110, 253, 0.2) 0%, rgba(102, 126, 234, 0.2) 100%);
        color: #e9ecef;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 13px;
        letter-spacing: 0.8px;
        padding: 18px 15px;
        border-bottom: 2px solid rgba(13, 110, 253, 0.4);
      }
      
      .package-upgrade-table tbody td {
        padding: 16px 15px;
        vertical-align: middle;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        color: #e9ecef;
        background: rgba(40, 40, 50, 0.5);
      }
      
      .package-upgrade-table tbody tr:nth-child(even) td {
        background: rgba(35, 35, 45, 0.5);
      }
      
      .package-upgrade-table tbody tr:hover td {
        background: rgba(50, 50, 65, 0.7);
      }
      
      /* Summary cards */
      .summary-card-package {
        background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
        border-radius: 12px;
        padding: 20px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        transition: all 0.3s ease;
      }
      
      .summary-card-package:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      }
      
      .summary-card-package.total-amount {
        border-left: 4px solid #198754;
      }
      
      .summary-card-package.total-upgrades {
        border-left: 4px solid #0d6efd;
      }
      
      .summary-card-package.average-amount {
        border-left: 4px solid #ffc107;
      }
      
      /* Responsive adjustments */
      @media (max-width: 768px) {
        .package-upgrade-table {
          font-size: 14px;
        }
        
        .package-name-badge, .package-amount-badge {
          font-size: 12px;
          padding: 4px 10px;
        }
      }
    `;
    document.head.appendChild(style);

    // Load Package Upgrade Data
    fetchPackageUpgrades();

    // Initialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons();
    }

    return () => {
      document.head.removeChild(style);
    };
  }, []);

    useEffect(() => {
        const sidebar = document.querySelector('.sidebar');
        const sidebarBody = document.querySelector('.sidebar-body');
        
        if (sidebar) {
          if (sidebarOpen) {
            sidebar.classList.remove('close');
          } else {
            sidebar.classList.add('close');
          }
        }
        
        if (sidebarBody) {
          sidebarBody.style.height = 'calc(100vh - 70px)';
          sidebarBody.style.overflowY = 'auto';
        }
    
        return () => {};
      }, [sidebarOpen]);
    
      useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
          .sidebar-body {
            height: calc(100vh - 70px);
            overflow-y: auto;
          }
          
          .sidebar-body::-webkit-scrollbar {
            width: 6px;
          }
          
          .sidebar-body::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 10px;
          }
          
          .sidebar-body::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 10px;
          }
          
          .sidebar-body::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
          
          .sidebar .nav-dropdown {
            list-style: none;
            padding-left: 0;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease;
          }
          
          .sidebar .nav-dropdown.show {
            max-height: 500px;
          }
          
          .sidebar .nav-dropdown-item {
            padding-left: 2.5rem;
          }
          
          .nav-item.has-dropdown .nav-link {
            position: relative;
            padding-right: 2.5rem;
          }
          
          .nav-item.has-dropdown .dropdown-icon {
            position: absolute;
            right: 1rem;
            top: 50%;
            transform: translateY(-50%);
            transition: transform 0.3s ease;
          }
          
          .nav-item.has-dropdown .nav-link[aria-expanded="true"] .dropdown-icon {
            transform: translateY(-50%) rotate(180deg);
          }
          
          .nav-item.has-dropdown .nav-dropdown {
            background: rgba(255, 255, 255, 0.05);
            border-left: 3px solid var(--primary-color, #0d6efd);
            margin-left: 1rem;
            border-radius: 0 0 8px 8px;
          }
          
          .nav-item.has-dropdown .nav-dropdown-item .nav-link {
            padding: 0.75rem 1rem 0.75rem 2rem;
            color: rgba(255, 255, 255, 0.8);
          }
          
          .nav-item.has-dropdown .nav-dropdown-item .nav-link:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
          }
          
          .nav-item.has-dropdown .link-title.nav-item.nav-category {
            font-weight: 600;
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .sidebar {
            transition: all 0.3s ease;
          }
        `;
        document.head.appendChild(style);
    
        return () => {
          document.head.removeChild(style);
        };
      }, []);
    

  // Toggle dropdown function
  const toggleDropdown = (key) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const fetchPackageUpgrades = async () => {
    setIsLoading(true);
    
    try {
        const result = await getPackageUpgradeListApi(authToken);

        if (result.status) {
            setPackageUpgrades(result.data);
            
            // Calculate initial totals
            const totalAmountValue = result.data.reduce((sum, item) => 
                sum + (parseFloat(item.package_amount) || 0), 0);
            const totalCountValue = result.data.length;
            
            setTotalAmount(totalAmountValue);
            setTotalCount(totalCountValue);
            setFilteredData(result.data);
            
            // Show success toast only if we have data
            if (result.data.length > 0) {
                // toast.success(result.message || "Package upgrades loaded successfully!");
            } else {
                // toast.info("No package upgrades found");
            }
        } else {
            // toast.error(result.message || "Failed to load package upgrades");
            
            // If session expired, redirect to login
            if (result.message.includes('Session expired') || result.message.includes('Unauthorized')) {
                setTimeout(() => {
                    navigate('/signin');
                }, 1500);
            }
        }
    } catch (error) {
        console.error("Error fetching package upgrades:", error);
        toast.error("Network error. Please check your connection.");
    } finally {
        setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    // Filters are automatically applied through useMemo
    toast.success("Filters applied successfully!");
  };

  const clearFilters = () => {
    setFilters({
      date: "",
      month: "",
      year: ""
    });
    toast.success("Filters cleared!");
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((item, index) => ({
      'SL No': index + 1,
      'ID': item.id,
      'Package Name': item.package_name,
      'Package Amount': `$${parseFloat(item.package_amount || 0).toFixed(2)}`,
      'Created At': item.created_at
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Package Upgrades');
    XLSX.writeFile(wb, `Package_Upgrades_${new Date().toISOString().split('T')[0]}.xlsx`);

    toast.success("Report exported to Excel successfully!");
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((item, index) => ({
      'SL No': index + 1,
      'ID': item.id,
      'Package Name': item.package_name,
      'Package Amount': `$${parseFloat(item.package_amount || 0).toFixed(2)}`,
      'Created At': item.created_at
    }));

    const csvContent = "data:text/csv;charset=utf-8,"
      + [Object.keys(exportData[0]).join(",")]
        .concat(exportData.map(row => Object.values(row).map(value =>
          `"${value}"`).join(",")))
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `package-upgrades-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Report exported to CSV successfully!");
  };

  // Format text for display
  const formatText = (text, maxLength = 20) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Get package type color
  const getPackageTypeColor = (packageName) => {
    if (packageName?.toLowerCase().includes('roi') || packageName?.toLowerCase().includes('qi')) {
      return 'bg-success'; // Green for ROI/QI Ecosystem
    } else if (packageName?.toLowerCase().includes('working') || packageName?.toLowerCase().includes('community')) {
      return 'bg-info'; // Blue for Working/Community Ecosystem
    } else {
      return 'bg-primary'; // Default blue
    }
  };

  return (
    <div className="main-wrapper">
      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? '' : 'close'}`}>
        <div className="sidebar-header">
          <a href="#" className="sidebar-brand" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
            <i className="me-2" data-lucide="atom"></i>
            Willow <span>Quantum</span>
          </a>
          <div className="sidebar-toggler" onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
       
       <Sidebar
                   isOpen={!sidebarOpen}
                   onClose={closeSidebar}
                 />
       
                 {/* Mobile backdrop */}
                 {isMobile && sidebarOpen && (
                   <div
                     className="sidebar-backdrop"
                     onClick={closeSidebar}
                   />
                 )}
      </nav>

      <div className="page-wrapper">
        <Navbar toggleSidebar={toggleSidebar}  isSidebarOpen={sidebarOpen}
            isMobile={isMobile}/>


        {/* Page Content */}
        <div className="page-content container-xxl">
          {/* Page Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div>
                <h4 className="fw-bold mb-1 text-white">
                  <i className="me-2" data-lucide="package"></i>
                  Plan Upgrade Report
                </h4>
                <p className="">View and manage all your package upgrade history</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="summary-card-package total-amount">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i data-lucide="dollar-sign" className="text-success" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div>
                    <h6 className=" mb-1">Total Investment</h6>
                    <h3 className="text-white mb-0">{formatAmount(totalAmount)}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="summary-card-package total-upgrades">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i data-lucide="package" className="text-primary" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div>
                    <h6 className=" mb-1">Total Upgrades</h6>
                    <h3 className="text-white mb-0">{totalCount}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="summary-card-package average-amount">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i data-lucide="trending-up" className="text-warning" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div>
                    <h6 className=" mb-1">Average Investment</h6>
                    <h3 className="text-white mb-0">
                      {totalCount > 0 ? formatAmount(totalAmount / totalCount) : '$0.00'}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card package-upgrade-card">
                <div className="card-body">
                  <h6 className="card-title text-white mb-3">
                    <i className="me-2" data-lucide="filter"></i>
                    Filter Package Upgrades
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label text-white">Date</label>
                      <input
                        type="date"
                        className="form-control bg-dark text-white border-secondary"
                        name="date"
                        value={filters.date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white">Month</label>
                      <select
                        className="form-select bg-dark text-white border-secondary"
                        name="month"
                        value={filters.month}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Months</option>
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i+1} value={i+1}>
                            {new Date(0, i).toLocaleString('default', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white">Year</label>
                      <select
                        className="form-select bg-dark text-white border-secondary"
                        name="year"
                        value={filters.year}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Years</option>
                        {Array.from({length: 5}, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return <option key={year} value={year}>{year}</option>;
                        })}
                      </select>
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={applyFilters}
                          disabled={isLoading}
                        >
                          <i className="me-2" data-lucide="search"></i>
                          {isLoading ? 'Filtering...' : 'Apply Filters'}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={clearFilters}
                          disabled={isLoading}
                        >
                          <i className="me-2" data-lucide="x"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Package Upgrade List */}
          <div className="row">
            <div className="col-12">
              <div className="card package-upgrade-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-baseline mb-3">
                    <h6 className="card-title mb-0 text-white">
                      <i className="me-2" data-lucide="list"></i>
                      Package Upgrade History
                    </h6>
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-outline-light btn-sm"
                        onClick={exportToCSV}
                        disabled={isLoading || filteredData.length === 0}
                      >
                        <i className="me-2" data-lucide="file-text"></i>
                        CSV
                      </button>
                      <button 
                        className="btn btn-outline-light btn-sm"
                        onClick={exportToExcel}
                        disabled={isLoading || filteredData.length === 0}
                      >
                        <i className="me-2" data-lucide="file-spreadsheet"></i>
                        Excel
                      </button>
                      {isLoading && (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table package-upgrade-table">
                      <thead>
                        <tr>
                          <th>SL No.</th>
                          <th>ID</th>
                          <th>Package Name</th>
                          <th>Package Amount</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan="5" className="text-center py-5">
                              <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="mt-3 text-white">Loading package upgrades...</p>
                            </td>
                          </tr>
                        ) : filteredData.length > 0 ? (
                          filteredData.map((upgrade, index) => (
                            <tr key={upgrade.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2" 
                                       style={{ width: '30px', height: '30px' }}>
                                    <span className="text-white fw-bold">{index + 1}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="package-id-badge">
                                  #{upgrade.id}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className={`package-name-badge ${getPackageTypeColor(upgrade.package_name)}`}>
                                    {formatText(upgrade.package_name, 25)}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className="package-amount-badge">
                                  {formatAmount(upgrade.package_amount)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="me-2" data-lucide="calendar" style={{ width: '16px', height: '16px' }}></i>
                                  <span className="text-white">{formatDate(upgrade.created_at)}</span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              <div className="">
                                <i data-lucide="package-x" className="icon-lg mb-3" style={{ width: '48px', height: '48px' }}></i>
                                <h5 className="text-white mb-2">No Package Upgrades Found</h5>
                                <p className=" mb-0">You haven't upgraded any packages yet.</p>
                                <button 
                                  className="btn btn-sm btn-link mt-2 text-primary"
                                  onClick={() => navigate('/upgrade-plan')}
                                >
                                  <i className="me-1" data-lucide="arrow-up-right"></i> Upgrade Now
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="footer d-flex flex-row align-items-center justify-content-center px-4 py-3 border-top small">
          <p className="text-secondary mb-1 mb-md-0">Copyright © 2025 Willow Quantum. All Right Reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default PlanUpgradeReport;